import type { AdminExhibition } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Button } from '@/shared/ui';

interface ExhibitionRowProps {
  readonly exhibition: AdminExhibition;
  /** 1부터 시작하는 노출 순번. */
  readonly order: number;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly isBusy: boolean;
  readonly onMove: (direction: 'UP' | 'DOWN') => void;
  readonly onEdit: () => void;
  readonly onToggleVisibility: () => void;
  readonly onManageProducts: () => void;
  readonly onDelete: () => void;
}

/** 숨김 / 공개 / 공개 · 상품 없음 — 공개인데 공개 상품이 0개면 고객 화면에 나오지 않는다. */
const statusLabelOf = (exhibition: AdminExhibition): string => {
  if (!exhibition.isVisible) {
    return '숨김';
  }

  return exhibition.publicProducts === null ? '공개 · 상품 없음' : '공개';
};

/** 지금 공개로 바꿀 수 없는 이유 — 공개 중이거나 조건을 다 채웠으면 null. */
const visibilityBlockerOf = (exhibition: AdminExhibition): string | null => {
  if (exhibition.isVisible) {
    return null;
  }

  if (exhibition.imageKey === null) {
    return '대표 이미지 필요';
  }

  return exhibition.publicProducts === null ? '공개 상품 필요' : null;
};

/** 묶인 공개 상품 중 최소 주문 수량 — 공개 상품이 없으면 "–". */
const minOrderQtyLabelOf = (exhibition: AdminExhibition): string =>
  exhibition.publicProducts === null ? '–' : `${exhibition.publicProducts.minOrderQty}개부터`;

/** 기획전 한 행 — 썸네일·이름·최소 수량·납기·공개 상품 수·상태와 관리 버튼. */
export const ExhibitionRow = ({
  exhibition,
  order,
  isFirst,
  isLast,
  isBusy,
  onMove,
  onEdit,
  onToggleVisibility,
  onManageProducts,
  onDelete,
}: ExhibitionRowProps): ReactElement => {
  const blocker = visibilityBlockerOf(exhibition);
  const visibilityLabel = exhibition.isVisible ? '숨기기' : '공개';

  return (
    <li className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
      <span className="w-6 text-center text-sm font-semibold tabular-nums">{order}</span>
      {exhibition.imageUrl !== null ? (
        <img
          src={exhibition.imageUrl}
          alt=""
          width={56}
          height={42}
          className="h-10 w-14 rounded object-cover"
        />
      ) : (
        <span aria-hidden className="h-10 w-14 rounded bg-muted" />
      )}

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{exhibition.title}</span>
        <span className="truncate text-xs text-muted-foreground">
          {minOrderQtyLabelOf(exhibition)} · {exhibition.leadTimeNote} · 공개 상품{' '}
          {exhibition.publicProducts?.count ?? 0}개
        </span>
      </span>

      <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{statusLabelOf(exhibition)}</span>
      {blocker !== null ? <span className="text-xs text-muted-foreground">{blocker}</span> : null}

      <span className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          disabled={isFirst || isBusy}
          onClick={() => onMove('UP')}
          aria-label={`${order}번 기획전 위로`}
        >
          위로
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isLast || isBusy}
          onClick={() => onMove('DOWN')}
          aria-label={`${order}번 기획전 아래로`}
        >
          아래로
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isBusy}
          onClick={onEdit}
          aria-label={`${order}번 기획전 수정`}
        >
          수정
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isBusy || blocker !== null}
          onClick={onToggleVisibility}
          aria-label={`${order}번 기획전 ${visibilityLabel}`}
        >
          {visibilityLabel}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isBusy}
          onClick={onManageProducts}
          aria-label={`${order}번 기획전 상품 관리`}
        >
          상품 관리
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive"
          disabled={isBusy}
          onClick={onDelete}
          aria-label={`${order}번 기획전 삭제`}
        >
          삭제
        </Button>
      </span>
    </li>
  );
};
